import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import "./Payments.css";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { IoWallet } from "react-icons/io5";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import riyalSymbol from "./images/riyal_symbol.svg";
import { useTranslation } from "react-i18next";
import { CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

function Payments() {
  const { t } = useTranslation("backend");
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedPlan, subscription_id, selectedPayment } =
    location.state || {};
  const [couponCode, setCouponCode] = useState("");
  const [discountedAmount, setDiscountedAmount] = useState(0);
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const [user, setUser] = useState({});
  const [toggleNext, setToggleNext] = useState(false);
  const [isPaid, setIsPaid] = useState(false);
  const [loading, setLoading] = useState(false);
  const token = Cookies.get("token");

  const [paymentMethod, setPaymentMethod] = useState(selectedPayment);
  const style = { layout: "vertical", disableMaxWidth: true };
  const [commission, setCommission] = useState(null);
  const [agreementChecked, setAgreementChecked] = useState({
    serviceAgreement: false,
    privacyPolicy: false,
  });
  const [couponApplied, setCouponApplied] = useState(false);
  const stripe = useStripe();
  const elements = useElements();

  const [clientSecret, setClientSecret] = useState("");
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeError, setStripeError] = useState(null);

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setAgreementChecked((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };

  // FIX 2: Single useEffect for init (removed the duplicate broken one)
  useEffect(() => {
    fetchCommission();
  }, [BASE_URL]);

  const fetchCommission = async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/commission`);
      if (!response.ok) {
        throw new Error("Failed to fetch commission data");
      }
      const data = await response.json();
      setCommission(data);
    } catch (error) {
      console.error("Error fetching commission data:", error);
    }
  };

  // FIX 3: Decode userEmail outside the useEffect so it's accessible in fetchUserDetails
  let userEmail = "";
  if (token) {
    try {
      const decodedToken = jwtDecode(token);
      userEmail = decodedToken.email;
    } catch (e) {
      console.error("Failed to decode token:", e);
    }
  }

  // Fetch user details
  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userEmail) return;
      try {
        const response = await fetch(`${BASE_URL}/edit/${userEmail}`);
        const result = await response.json();
        if (response.ok) {
          setUser(result.user);
        } else alert(result.error);
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    };
    fetchUserDetails();
  }, [BASE_URL, token]);

  const handleNext = () => setToggleNext(true);

  const createOrder = async () => {
    try {
      const response = await fetch(`${BASE_URL}/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userEmail: user.email,
          planName: selectedPlan.plan_name,
          amounts: totalWithCommission - discountedAmount,
          duration: selectedPlan.duration,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to create the order");
      }

      const order = await response.json();
      return order.id;
    } catch (error) {
      console.error("Error creating order:", error);
      alert("There was an issue creating the order. Please try again.");
    }
  };

  const onApprove = async (data) => {
    try {
      const response = await fetch(`${BASE_URL}/capture-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          orderID: data.orderID,
          userEmail: user.email,
          planName: selectedPlan.plan_name,
          amounts: totalWithCommission - discountedAmount,
          duration: selectedPlan.duration,
          subscription_id: subscription_id,
          discounted_amount: discountedAmount,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to capture the order");
      }

      setIsPaid(true);
      window.close();
    } catch (error) {
      console.error("Error capturing the order:", error);
      alert("There was an issue processing the payment. Please try again.");
    }
  };

  // Guard against null commission on first render
  const commissions = commission != null
    ? ((selectedPlan.amount * commission) / 100).toFixed(2)
    : "0.00";
  const totalWithCommission = (
    Number(commissions) + Number(selectedPlan.amount)
  ).toFixed(2);

  const handleStripePayment = async (e) => {
    e.preventDefault();
    if (!agreementChecked.serviceAgreement || !agreementChecked.privacyPolicy) {
      toast.warn(t("agreeTermsRequired"));
      return;
    }

    if (!stripe || !elements) return;

    setStripeLoading(true);

    const res = await fetch(`${BASE_URL}/api/create-payment-intent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        amount: selectedPlan.amount - discountedAmount,
        email: user.email,
        subscription_id,
      }),
    });

    const { clientSecret } = await res.json();

    const { error, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            email: user.email,
            name: user.name,
          },
        },
      }
    );

    if (error) {
      setStripeError(error.message);
      setStripeLoading(false);
      return;
    }

    if (paymentIntent.status === "succeeded") {
      await fetch(`${BASE_URL}/api/stripe-success`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paymentIntentId: paymentIntent.id,
          email: user.email,
          planName: selectedPlan.plan_name,
          amount: selectedPlan.amount,
          duration: selectedPlan.duration,
          subscription_id,
          discounted_amount: discountedAmount,
        }),
      });

      toast.success("Payment successful");
      setIsPaid(true);
    }

    setStripeLoading(false);
  };

  const handleWallet = async () => {
    if (!agreementChecked.serviceAgreement || !agreementChecked.privacyPolicy) {
      toast.warn(t("agreeTermsRequired"));
      return;
    }
    if (!window.confirm(t("confirmPayWithWallet"))) {
      return;
    }
    try {
      const response = await fetch(`${BASE_URL}/api/save-wallet`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: user.email,
          planName: selectedPlan.plan_name,
          amount: selectedPlan.amount,
          duration: selectedPlan.duration,
          domain_name: selectedPlan.domain_name,
          subscription_id: subscription_id,
          discounted_amount: discountedAmount,
        }),
      });
      const result = await response.json();
      if (response.ok) {
        toast.success(result.message);
        setIsPaid(true);
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleRequestRecharge = () => {
    navigate("/dashboard/walletrecharge");
  };

  const handleApplyCoupon = async () => {
    if (!couponCode) {
      toast.warn(t("enterCouponCode"));
      return;
    }
    try {
      const response = await fetch(`${BASE_URL}/api/apply-coupon`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ couponCode }),
      });
      const result = await response.json();
      if (response.ok) {
        setCouponApplied(true);
        setDiscountedAmount(result.couponDetails.discount);
        toast.success(t("couponApplied"));
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      console.error("Error applying coupon:", error);
      toast.error("Failed to apply the coupon. Please try again.");
    }
  };

  return (
    // PayPalScriptProvider must wrap the whole tree — never put it inside a
    // conditional or it will remount (restarting SDK load) every render.
     <PayPalScriptProvider
options={{
  "client-id": process.env.REACT_APP_PAYPAL_CLIENT_ID,
  currency: "USD",
  components: "buttons",
  intent: "capture",
}}
     >
    <div className="edit-contain">
      {!isPaid && <h2>{t("payment")}</h2>}
      {!toggleNext ? (
        <div className="container-edit details-container payments">
          <div className="edit-form">
            <div className="form-group">
              <label>{t("name")}:</label>
              <p>{user.name || ""}</p>
            </div>
            <div className="form-group">
              <label>{t("email")}:</label>
              <p>{user.email || ""}</p>
            </div>
            <div className="form-group">
              <label>{t("phone")}:</label>
              <p>{user.phone || ""}</p>
            </div>
            <div className="form-group">
              <label>{t("status")}:</label>
              <div
                style={{
                  color: user.status === 1 ? "green" : "brown",
                  fontWeight: "bold",
                }}
              >
                {user.status === 1 ? "Enabled" : "Disabled"}
              </div>
            </div>
            <div className="form-group">
              <label>{t("address")}:</label>
              <p>{user.address || ""}</p>
            </div>
            <div className="save-back-btn-class">
              <button
                type="button"
                className="back-btn"
                onClick={() => navigate(-1)}
              >
                {t("back")}
              </button>
              <button
                type="button"
                className="submit-button"
                onClick={handleNext}
                disabled={loading}
              >
                {loading ? t("processing_payment") : t("next_for_payment")}
              </button>
            </div>
          </div>
          <p className="para-text">
            {t("want_to_update1")}
            <Link to="/dashboard/profile"> {t("want_to_update2")}</Link>{" "}
            {t("want_to_payment3")}
          </p>
        </div>
      ) : (
        <div className="payment-confirmation">
          {!isPaid ? (
            <>
              <div className="payment-section">
                <div className="inside-payment-section">
                  <div className="payment-methods">
                    <span className="section-title">{t("payment_method")}</span>
                    <div className="payment-options">
                      <div className="option">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="paypal"
                          checked={paymentMethod === "paypal"}
                          onChange={() => setPaymentMethod("paypal")}
                          id="paypal"
                        />
                        <label htmlFor="paypal">
                          {t("pay_using_paypal")} (+{commission}%)
                        </label>
                      </div>
                      <div className="option">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="wallet"
                          checked={paymentMethod === "wallet"}
                          onChange={() => setPaymentMethod("wallet")}
                          id="wallet"
                        />
                        <label htmlFor="wallet">{t("pay_using_wallet")}</label>
                      </div>
                      <div className="option">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="stripe"
                          checked={paymentMethod === "stripe"}
                          onChange={() => setPaymentMethod("stripe")}
                          id="stripe"
                        />
                        <label htmlFor="stripe">{t("pay_stripe")}</label>
                      </div>
                    </div>
                    {paymentMethod === "wallet" && (
                      <div
                        style={{
                          color: user.wallet_balance < 0 ? "red" : "black",
                          textAlign: "center",
                        }}
                      >
                        {t("available_wallet")} ={" "}
                        <img src={riyalSymbol} height={"20px"} alt="riyal" />{" "}
                        {user.wallet_balance}
                      </div>
                    )}
                  </div>

                  <div className="payment-methods" style={{ marginTop: "10px" }}>
                    <span className="section-title">{t("request_wallet")}</span>
                    <div className="payment-options">
                      <button
                        className="wallet-button buttons"
                        onClick={handleRequestRecharge}
                      >
                        <IoWallet className="icon" /> {t("request_wallet")}
                      </button>
                    </div>
                  </div>

                  <div className="service-agreements">
                    <span className="section-title">
                      {t("service_agreements")}
                    </span>
                    <div className="agreements-options">
                      <div className="option services">
                        <input
                          type="checkbox"
                          name="serviceAgreement"
                          checked={agreementChecked.serviceAgreement}
                          onChange={handleCheckboxChange}
                          required
                          id="agree"
                        />
                        <label htmlFor="agree">
                          {t("i_agree")} <a href="#">{t("i_agree1")}</a>
                        </label>
                      </div>
                      <div className="option services">
                        <input
                          type="checkbox"
                          name="privacyPolicy"
                          checked={agreementChecked.privacyPolicy}
                          onChange={handleCheckboxChange}
                          required
                          id="agrees"
                        />
                        <label htmlFor="agrees">
                          {t("i_agree2")} <a href="#">{t("i_agree3")}</a>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="order-summary">
                  <span className="section-title">{t("order_summary")}</span>
                  <div className="inside-order-summary">
                    <div className="summary-item">
                      <span style={{ fontWeight: "bold", color: "#0f4b60" }}>
                        {t("total_amount")}
                      </span>{" "}
                      <span style={{ fontWeight: "bold", color: "#FE9900" }}>
                        <img src={riyalSymbol} height={"20px"} alt="riyal" />{" "}
                        {selectedPlan.amount}
                      </span>
                    </div>
                    {paymentMethod === "paypal" ? (
                      <>
                        <div className="summary-item">
                          <span style={{ fontWeight: "bold", color: "#0f4b60" }}>
                            {t("processing_charges")} ({commission}%)
                          </span>{" "}
                          <span style={{ fontWeight: "bold", color: "#FE9900" }}>
                            <img src={riyalSymbol} height={"20px"} alt="riyal" />{" "}
                            {commissions}
                          </span>
                        </div>
                        {couponApplied ? (
                          <div style={{ color: "#2ECC71", fontWeight: "bold" }}>
                            {t("coupon_applied")}{" "}
                            <img src={riyalSymbol} height={"20px"} alt="riyal" />{" "}
                            {discountedAmount}
                          </div>
                        ) : (
                          <>
                            <span style={{ fontWeight: "bold", color: "#0f4b60" }}>
                              {t("use_coupon")}
                            </span>
                            <input
                              type="text"
                              placeholder="Enter coupon code"
                              value={couponCode}
                              onChange={(e) => setCouponCode(e.target.value)}
                              style={{
                                padding: "5px",
                                borderRadius: "4px",
                                border: "1px solid #ccc",
                                fontSize: "14px",
                                marginLeft: "4px",
                              }}
                              disabled={couponApplied}
                            />
                            <button
                              onClick={handleApplyCoupon}
                              style={{
                                padding: "5px 10px",
                                backgroundColor: couponApplied ? "#2ECC71" : "#0f4b60",
                                color: "#fff",
                                border: "none",
                                borderRadius: "4px",
                                cursor: couponApplied ? "default" : "pointer",
                                marginLeft: "1px",
                              }}
                              disabled={couponApplied}
                            >
                              {couponApplied ? t("coupon_applied1") : t("apply")}
                            </button>
                          </>
                        )}
                        <div className="summary-item total">
                          <span style={{ fontWeight: "bold", color: "#0f4b60" }}>
                            {t("total_amount_payable")}
                          </span>{" "}
                          <span style={{ fontWeight: "bold", color: "#FE9900" }}>
                            <img src={riyalSymbol} height={"20px"} alt="riyal" />{" "}
                            {totalWithCommission - discountedAmount}.00
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="summary-item">
                          <span style={{ fontWeight: "bold", color: "#0f4b60" }}>
                            {t("processing_charges")}
                          </span>{" "}
                          <span style={{ fontWeight: "bold", color: "#FE9900" }}>
                            0
                          </span>
                        </div>

                        <div className="summary-item">
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "10px",
                            }}
                          >
                            {couponApplied ? (
                              <div style={{ color: "#2ECC71", fontWeight: "bold" }}>
                                {t("coupon_applied")}{" "}
                                <img src={riyalSymbol} height={"20px"} alt="riyal" />{" "}
                                {discountedAmount}
                              </div>
                            ) : (
                              <>
                                <span style={{ fontWeight: "bold", color: "#0f4b60" }}>
                                  {t("use_coupon")}
                                </span>
                                <input
                                  type="text"
                                  placeholder={t("enter_coupon_code")}
                                  value={couponCode}
                                  onChange={(e) => setCouponCode(e.target.value)}
                                  style={{
                                    padding: "5px",
                                    borderRadius: "4px",
                                    border: "1px solid #ccc",
                                    fontSize: "14px",
                                  }}
                                  disabled={couponApplied}
                                />
                                <button
                                  onClick={handleApplyCoupon}
                                  style={{
                                    padding: "5px 10px",
                                    backgroundColor: couponApplied ? "#2ECC71" : "#0f4b60",
                                    color: "#fff",
                                    border: "none",
                                    borderRadius: "4px",
                                    cursor: couponApplied ? "default" : "pointer",
                                  }}
                                  disabled={couponApplied}
                                >
                                  {couponApplied ? t("coupon_applied1") : t("apply")}
                                </button>
                              </>
                            )}
                          </div>
                        </div>

                        <div className="summary-item total">
                          <span style={{ fontWeight: "bold", color: "#0f4b60" }}>
                            {t("total_amount_payable")}
                          </span>{" "}
                          <span style={{ fontWeight: "bold", color: "#FE9900" }}>
                            <img src={riyalSymbol} height={"20px"} alt="riyal" />{" "}
                            {selectedPlan.amount - discountedAmount}.00
                          </span>
                        </div>
                      </>
                    )}
                    <div className="payment-method-details">
                        {/* PayPalScriptProvider is at the top of the tree — just render buttons here */}
                        {paymentMethod === "paypal" && (
                          <PayPalButtons
                            style={style}
                            createOrder={createOrder}
                            onApprove={onApprove}
                          />
                        )}
                        {paymentMethod === "wallet" && (
                          <div>
                            <button
                              className="wallet-button buttons"
                              onClick={handleWallet}
                            >
                              <IoWallet className="icon" />{" "}
                              {t("continue_wallet")}
                            </button>
                          </div>
                        )}
                        {paymentMethod === "stripe" && (
                          <form
                            onSubmit={handleStripePayment}
                            className="stripe-form"
                          >
                            <div className="card-box">
                              <CardElement />
                            </div>

                            {stripeError && (
                              <p style={{ color: "red", marginTop: "8px" }}>
                                {stripeError}
                              </p>
                            )}

                            <button
                              type="submit"
                              className="buttons"
                              disabled={!stripe || stripeLoading}
                              style={{ marginTop: "15px" }}
                            >
                              {stripeLoading ? "Processing..." : "Pay Now"}
                            </button>
                          </form>
                        )}
                      </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div>
              <h2>{t("thanks")}</h2>
              <p style={{ color: "green", textAlign: "center" }}>
                {t("sucess_order")}
              </p>
            </div>
          )}
        </div>
      )}
      <ToastContainer position="top-center" />
    </div>
    </PayPalScriptProvider>
  );
} // FIX 5: Added missing closing brace for the component function

export default Payments;